import _ from 'lodash';

import { Media } from './model/components/Media';
import { PostData } from './model/PostTypes';
import { PortalPublication } from './PortalPublication';
import { Modal } from '../shared';
import {
    ArticlePage,
    POP_TEXT_V2_ROW_SEPARATOR,
    popTextV2ToPopText,
} from '@lifeserver/shared';

import {
    PortalApi,
    PortalCategoryDTO,
    PortalFeedDTO,
    PortalPostSlimDTO,
    PortalSettingsDTO,
    Post,
    PublicationDTO,
    UpdatePostDTO,
} from '../hooks/portal';

import { errorLikeToMessage } from '../utils/error';
import { copyMedias } from '../utils/upload';

import { MediaType } from '../types/enums/mediatype.enum';

import { Environment } from '../types/environmentTypes';
import { isRecord } from '../types/isRecord';

export type PublicationStatus = 'scheduled' | 'published' | 'expired';

export type DateLike = Date | number | string;

export const getPublicationStatus = (
    publicationRange: {
        startTime: DateLike;
        endTime: DateLike;
    },
    time: number = Date.now(),
): PublicationStatus => {
    const start = new Date(publicationRange.startTime).getTime();

    const end = new Date(publicationRange.endTime).getTime();
    if (time >= end) {
        return 'expired';
    }
    if (time < start) {
        return 'scheduled';
    }
    return 'published';
};

export const getOverlappingPublications = (
    publications: Pick<PublicationDTO, 'id' | 'startTime' | 'endTime'>[],
    overlapLimit: number,
): string[] => {
    const events = [];
    for (const publication of publications) {
        events.push({
            time: new Date(publication.startTime).getTime(),
            isStart: true,
            id: publication.id,
        });
        if (publication.endTime) {
            events.push({
                time: new Date(publication.endTime).getTime(),
                isStart: false,
                id: publication.id,
            });
        }
    }
    events.sort((a, b) => {
        if (a.time !== b.time) {
            return a.time - b.time;
        }

        // Prefer 'end' events over 'start' events to not think they are overlapping if start == end
        if (a.isStart == b.isStart) {
            return 0;
        }
        return a.isStart ? 1 : -1;
    });

    const overlappingIds = new Set<string>();
    const activePublications = new Set<string>();

    for (const event of events) {
        if (event.isStart) {
            activePublications.add(event.id);
        } else {
            activePublications.delete(event.id);
        }
        if (activePublications.size > overlapLimit) {
            activePublications.forEach((id) => overlappingIds.add(id));
        }
    }

    return Array.from(overlappingIds);
};

export const prepareGamePayload = (
    postDatas: PostData[],
    lang: string = 'en',
    localizations: Record<string, Record<string, string>> = {},
    settings?: PortalSettingsDTO,
) => {
    const resolveTranslation = (value: Record<string, unknown>): string => {
        const key = typeof value['$key'] === 'string' ? value['$key'] : null;

        if (key && localizations[key]) {
            const forLang = localizations[key]?.[lang];
            if (typeof forLang === 'string') return forLang;

            const forEn = localizations[key]?.['en'];
            if (typeof forEn === 'string') return forEn;
        }

        // 2) Legacy / inline format: TranslationTable still has en/sv/... on it
        const inline = value[lang] ?? value['en'];
        if (typeof inline === 'string') return inline;

        return '';
    };

    const translatedPosts = postDatas.map((postData: PostData, i) => ({
        ..._.cloneDeepWith(postData, (value: unknown) => {
            if (isRecord(value) && value['$type'] === 'TranslationTable') {
                return resolveTranslation(value);
            }
            return undefined;
        }),
        postId: `post-${i}`,
    }));

    const migratedPosts = translatedPosts.map((postData: PostData, i) => ({
        ..._.cloneDeepWith(postData, (value: unknown) => {
            if (value && _.isObject(value)) {
                const popTextV2s = (value as ArticlePage).popTextV2s;
                if (popTextV2s) {
                    return {
                        ...value,
                        popTexts: popTextV2s.map((popTextV2) => {
                            return popTextV2ToPopText(popTextV2);
                        }),
                        popTextV2s: undefined,
                    };
                }
            }
            return undefined;
        }),
        postId: `post-${i}`,
    }));

    return {
        categories: [
            {
                categoryId: 'Preview Category',
                categoryName: 'Preview Category',
                categoryType: 'Manual',
                posts: migratedPosts,
                image: null,
                activeColor: null,
                inactiveColor: null,
                tint: null,
            },
        ],
        featured: {
            posts: [],
        },
        settings: settings ?? undefined,
    } satisfies PortalFeedDTO;
};

const copyPortalMedia = async (media: Media, targetEnv: Environment) => {
    const fileName = new URL(media.url).pathname.substring(1);
    let resourceId = fileName.replaceAll('{0}', ''); // Sometimes, due to earlier bugs, stray {0} was "kept" in the filename that shouldn't really be there. Let's remove them to avoid potential issues with how to deal with multiple replacement variables in the name

    if (media.mediaType === 'Image') {
        // For images, copyMedias re-adds extension later
        const extensionIndex = fileName.indexOf('.');
        resourceId = resourceId.substring(0, extensionIndex);
    }

    const [copiedMedia] = await copyMedias(
        [
            {
                type:
                    media.mediaType === 'Video'
                        ? MediaType.VIDEO
                        : MediaType.PORTAL,
                url: media.url,
            },
        ],
        resourceId,
        targetEnv,
        _.noop,
    );
    return copiedMedia;
};

export const copyPortalCategory = async (
    portalApi: PortalApi,
    category: PortalCategoryDTO,
    targetEnv: Environment,
) => {
    let image = category.image;

    if (image) {
        const updatedMedia = await copyPortalMedia(
            {
                mediaType: 'Image',
                url: image,
            },
            targetEnv,
        );
        image = updatedMedia.media.url;
    }

    await portalApi.updateCategory({
        categoryId: category.id,
        upsertPortalCategoryDTO: {
            name: category.name,
            abTestCase: category.abTestCase ?? undefined,
            image: image,
            inactiveColor: category.inactiveColor ?? undefined,
            activeColor: category.activeColor ?? undefined,
            position: category.position,
            tint: category.tint,
            variant: category.variant ?? undefined,
            type: category.type,
            schedule: category.schedule ?? undefined,
        },
        basePath: targetEnv.basepath,
    });
};

export const copyPortalPosts = async (
    portalApi: PortalApi,
    categoryId: string | 'featured',
    posts: PortalPostSlimDTO[],
    targetEnv: Environment,
) => {
    const targetEnvPublications = await (categoryId === 'featured'
        ? portalApi
              .getFeaturedPosts({
                  basePath: targetEnv.basepath,
              })
              .then((result) => result.featuredPosts)
        : portalApi
              .getScheduleByCategoryId({
                  basePath: targetEnv.basepath,
                  categoryId,
              })
              .then((result) => result.publications));

    const targetEnvPostsMap = targetEnvPublications.reduce(
        (map, next) => map.set(next.post.id, next.post),
        new Map<string, PortalPostSlimDTO>(),
    );
    const allChangedPosts = posts.filter((post) => {
        const targetEnvPost = targetEnvPostsMap.get(post.id);
        if (!targetEnvPost) {
            return true;
        }

        return (
            new Date(targetEnvPost.modifiedAt).getTime() <
            new Date(post.modifiedAt).getTime()
        );
    });
    const chunkedChangedPosts = _.chunk(allChangedPosts, 4);

    for (const changedPosts of chunkedChangedPosts) {
        const postMap = new Map(changedPosts.map((post) => [post.id, post]));

        const copyMediaPromises = changedPosts
            .flatMap((post) => getPostMedia(post.payload))
            .map(async (media) => {
                const copiedMedia = await copyPortalMedia(media, targetEnv);
                return [media.url, copiedMedia.media.url] as const;
            });

        const mediaUrlPairs = await Promise.all(copyMediaPromises);
        const mediaMap = new Map<string, string>(mediaUrlPairs);

        const putPostDtos: Array<{ id: string; dto: UpdatePostDTO }> =
            Array.from(postMap.values()).map((post) => ({
                id: post.id,
                dto: {
                    payload: _.cloneDeepWith(post.payload, (value) =>
                        mediaMap.get(value),
                    ),
                    templateId: post.templateId,
                    state: post.state,
                    notes: post.notes,
                    minimumVersion: post.minimumVersion,
                },
            }));

        for (const { id, dto } of putPostDtos) {
            await portalApi.updatePost({
                postId: id,
                updatePostDto: dto,
                basePath: targetEnv.basepath,
            });
        }
    }
};

/**
 * Copy a single portal post to another environment. Media referenced by the
 * post is copied first, then the post is either created (if missing in the
 * target env) or updated (if it already exists there).
 */
export const copyPortalPostToEnv = async (
    portalApi: PortalApi,
    post: Post,
    targetEnv: Environment,
): Promise<void> => {
    // Copy every media asset referenced by the post to the target env and
    // build a src → dst URL map so we can rewrite the payload afterwards.
    const mediaList = getPostMedia(post.payload);
    const mediaUrlPairs = await Promise.all(
        mediaList.map(async (media) => {
            const copied = await copyPortalMedia(media, targetEnv);
            return [media.url, copied.media.url] as const;
        }),
    );
    const mediaMap = new Map<string, string>(mediaUrlPairs);

    const payload = _.cloneDeepWith(post.payload, (value: unknown) =>
        typeof value === 'string' ? mediaMap.get(value) : undefined,
    );

    // Detect whether the post already exists in the target env. `fetchPostById`
    // throws on 404 (and on network errors), so we probe and swallow the 404.
    let exists = false;
    try {
        await portalApi.fetchPostById({
            id: post.id,
            basePath: targetEnv.basepath,
        });
        exists = true;
    } catch (e) {
        const status = (e as { response?: { status?: number } })?.response
            ?.status;
        if (status !== 404) {
            throw e;
        }
    }

    if (exists) {
        await portalApi.updatePost({
            postId: post.id,
            updatePostDto: {
                payload,
                templateId: post.templateId,
                state: post.state,
                notes: post.notes,
                // Tags are upserted by name on the server, so this creates
                // any missing tags in the target env and links them to the post.
                tags: post.tags ?? [],
                minimumVersion: post.minimumVersion,
            },
            basePath: targetEnv.basepath,
        });
    } else {
        await portalApi.createPost({
            createPostDto: {
                id: post.id,
                templateId: post.templateId,
                payload,
                state: post.state,
                notes: post.notes ?? '',
                tags: post.tags ?? [],
                minimumVersion: post.minimumVersion,
            },
            basePath: targetEnv.basepath,
        });
    }
};

export const getPostMedia = (postData: PostData): Media[] => {
    switch (postData.$type) {
        case 'Article':
            return postData.pages
                .map((page) => page.background)
                .filter(
                    (media: Media | undefined): media is Media =>
                        media !== undefined,
                );

        case 'Video':
            return postData.videoUrl
                ? [
                      {
                          url: postData.videoUrl,
                          mediaType: 'Video',
                      },
                  ]
                : [];
        case 'ShopLink':
            return postData.coverMedia ? [postData.coverMedia] : [];
        case 'VoteSliderPoll':
        case 'ThisOrThatPoll':
            return (postData.pages || [])
                .map((page) => page?.background)
                .filter((media): media is Media => media !== undefined);
        case 'TilePuzzle':
            return [
                postData.gameTextureUrl,
                postData.winViewData?.media,
            ].filter((item): item is Media => {
                if (typeof item === 'string') {
                    return false;
                }
                return item !== undefined;
            });
        default:
            return [];
    }
};

// Regular expression to match all common line breaks and Unicode separators
// \r\n : Matches CRLF (Windows)
// \n   : Matches LF (Unix/macOS)
// \r   : Matches CR (older Macs) - needs to be separate and after \r\n to avoid partial match
// \u2028 : Matches LINE SEPARATOR (LS)
// \u2029 : Matches PARAGRAPH SEPARATOR (PS)
// \u000B : Matches VERTICAL TAB (VT)
// \u000C : Matches FORM FEED (FF)
// \u0085 : Matches NEXT LINE (NEL)

// The order is important, especially for \r\n before \r or \n.
// eslint-disable-next-line no-control-regex
const separatorRegex = /(\r\n|\n|\r|\u2028|\u2029|\u000B|\u000C|\u0085)/g;
export const transformLinebreaks = (value: string) => {
    return value.replaceAll(separatorRegex, POP_TEXT_V2_ROW_SEPARATOR);
};

export function compactSchedule(
    publications: PortalPublication[],
): PortalPublication[] {
    const compactedSchedule: PortalPublication[] = [];

    const sortedPublications = publications.toSorted(
        (p1, p2) => p1.position - p2.position,
    );
    for (const publication of sortedPublications) {
        const interval = [
            new Date(publication.startTime).getTime(),
            new Date(publication.endTime).getTime(),
        ] as const;
        for (let lane = 0; lane < 100; lane++) {
            const hasOverlap = compactedSchedule.some(
                (p) =>
                    p.position === lane &&
                    isOverlapping(interval, [
                        new Date(p.startTime).getTime(),
                        p.endTime ? new Date(p.endTime).getTime() : Infinity,
                    ]),
            );
            if (!hasOverlap) {
                compactedSchedule.push({
                    ...publication,
                    position: lane,
                });
                break;
            }
        }
    }

    return compactedSchedule;
}

export function isOverlapping(
    [x1, y1]: readonly [number, number],
    [x2, y2]: readonly [number, number],
): boolean {
    return x1 < y2 && x2 < y1;
}

/**
 * Shows a name-prompt modal and calls `onCreatePost`, re-prompting on 409 conflicts.
 * Calls `onSuccess` with the created post on success, or returns on cancel.
 */
export async function promptAndDuplicatePost(
    initialId: string,
    onCreatePost: (newName: string) => Promise<Post>,
    onSuccess: (post: Post) => void,
): Promise<void> {
    let promptValue = `${initialId}-copy`;
    let promptText = 'Name for the new post:';

    while (true) {
        const newName = await Modal.prompt({
            heading: 'Duplicate post',
            text: promptText,
            value: promptValue,
        });
        if (!newName) return;

        try {
            const newPost = await onCreatePost(newName);
            onSuccess(newPost);
            return;
        } catch (e) {
            if (
                (e as { response?: { status?: number } }).response?.status ===
                409
            ) {
                promptValue = newName;
                promptText = `"${newName}" is already taken. Choose a different name:`;
                continue;
            }
            await Modal.alert({
                heading: 'Duplication failed',
                text: `Failed to duplicate post: ${errorLikeToMessage(e)}`,
            });
            return;
        }
    }
}
